import { useState } from "react";
import { Send, Phone, ArrowRight } from "lucide-react";
import { SERVICES } from "@/data/services";
import { LOCATIONS } from "@/data/locations";
import { BUSINESS } from "@/data/businessInfo";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LeadCaptureForm = ({ title = "Get Your Free Estimate", subtitle = "Tell us about your project and we'll get back to you within one business day.", compact = false }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", city: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { toast.error("Please provide your name and phone number."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/contacts`, form);
      setSent(true);
      toast.success("Request submitted! We'll contact you shortly.");
    } catch {
      toast.error("Something went wrong. Please call us directly.");
    }
    setLoading(false);
  };

  if (sent) {
    return (
      <div className={`bg-white rounded-2xl border border-[#6B3A1F]/10 ${compact ? 'p-6' : 'p-8'} text-center`} data-testid="lead-form-success">
        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
          <Send className="w-5 h-5 text-green-600" />
        </div>
        <h3 className="text-lg font-bold text-[#401303] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Request Received</h3>
        <p className="text-sm text-[#6B3A1F]" style={{ fontFamily: 'Manrope, sans-serif' }}>We'll contact you shortly. For immediate help, call <a href={`tel:${BUSINESS.phoneRaw}`} className="text-[#D97904] font-semibold">{BUSINESS.phone}</a></p>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-2xl border border-[#6B3A1F]/10 shadow-lg ${compact ? 'p-5' : 'p-6 lg:p-8'}`} data-testid="lead-capture-form">
      <h3 className="text-xl font-bold text-[#401303] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h3>
      <p className="text-sm text-[#6B3A1F] mb-5" style={{ fontFamily: 'Manrope, sans-serif' }}>{subtitle}</p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className={compact ? "space-y-4" : "grid sm:grid-cols-2 gap-4"}>
          <div>
            <label className="block text-xs font-semibold text-[#401303] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Full Name *</label>
            <input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-4 py-3 border-2 border-[#6B3A1F]/15 rounded-xl focus:border-[#D97904] focus:outline-none text-sm" placeholder="Your name" required data-testid="lead-name" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#401303] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Phone *</label>
            <input type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="w-full px-4 py-3 border-2 border-[#6B3A1F]/15 rounded-xl focus:border-[#D97904] focus:outline-none text-sm" placeholder="(772) 000-0000" required data-testid="lead-phone" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#401303] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Email</label>
            <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-4 py-3 border-2 border-[#6B3A1F]/15 rounded-xl focus:border-[#D97904] focus:outline-none text-sm" placeholder="your@email.com" data-testid="lead-email" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#401303] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Service Needed</label>
            <select value={form.service} onChange={e => setForm({...form, service: e.target.value})} className="w-full px-4 py-3 border-2 border-[#6B3A1F]/15 rounded-xl focus:border-[#D97904] focus:outline-none text-sm bg-white" data-testid="lead-service">
              <option value="">Select a service</option>
              {SERVICES.map(s => <option key={s.id} value={s.title}>{s.title}</option>)}
              <option value="Other">Other</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#401303] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Service Area</label>
          <select value={form.city} onChange={e => setForm({...form, city: e.target.value})} className="w-full px-4 py-3 border-2 border-[#6B3A1F]/15 rounded-xl focus:border-[#D97904] focus:outline-none text-sm bg-white" data-testid="lead-city">
            <option value="">Select your area</option>
            {LOCATIONS.map(l => <option key={l.id} value={l.name}>{l.name}, FL</option>)}
            <option value="Other">Other Area</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#401303] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Brief Description</label>
          <textarea value={form.message} onChange={e => setForm({...form, message: e.target.value})} rows={3} className="w-full px-4 py-3 border-2 border-[#6B3A1F]/15 rounded-xl focus:border-[#D97904] focus:outline-none text-sm resize-none" placeholder="Describe what you need help with..." data-testid="lead-message" />
        </div>
        <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 btn-gradient rounded-full text-sm disabled:opacity-50" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="lead-submit">
          {loading ? "Submitting..." : "Request Free Estimate"} <ArrowRight className="w-4 h-4" />
        </button>
        <p className="text-center text-xs text-[#6B3A1F]/60" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Or call <a href={`tel:${BUSINESS.phoneRaw}`} className="text-[#D97904] font-semibold">{BUSINESS.phone}</a> for immediate assistance
        </p>
      </form>
    </div>
  );
};

export const LeadCaptureSection = () => (
  <section className="bg-[#FAFAFA] py-14 lg:py-20" data-testid="lead-capture-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <div className="grid lg:grid-cols-2 gap-10 items-start">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#D97904] mb-3 block" style={{ fontFamily: 'Outfit, sans-serif' }}>Free Estimate</span>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#401303] tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Ready to Schedule Your Electrical Service?</h2>
          <p className="text-[#6B3A1F] leading-relaxed mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>Fill out the form and Anthony will personally review your request and get back to you within one business day with an honest estimate. No pressure, no surprise fees.</p>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#D97904]/10 flex items-center justify-center flex-shrink-0"><Phone className="w-5 h-5 text-[#D97904]" /></div>
              <div>
                <p className="text-sm font-semibold text-[#401303]" style={{ fontFamily: 'Outfit, sans-serif' }}>Call or Text Anytime</p>
                <a href={`tel:${BUSINESS.phoneRaw}`} className="text-sm text-[#D97904] font-semibold">{BUSINESS.phone}</a>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#D97904]/10 flex items-center justify-center flex-shrink-0"><Send className="w-5 h-5 text-[#D97904]" /></div>
              <div>
                <p className="text-sm font-semibold text-[#401303]" style={{ fontFamily: 'Outfit, sans-serif' }}>15+ Years Experience</p>
                <p className="text-sm text-[#6B3A1F]">Licensed, insured, BBB A-rated</p>
              </div>
            </div>
          </div>
        </div>
        <LeadCaptureForm />
      </div>
    </div>
  </section>
);
